import { useCallback, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { useLaunchAggregatorGameMutation } from '@/store/api/aggregatorApi';
import { useAppSelector } from '@/hooks';

/**
 * Launches an aggregator game: asks the backend for a launchUrl (which proxies
 * /v1/launch) and opens it in the existing casino playground iframe page.
 */
export const useAggregatorLauncher = () => {
  const navigate = useNavigate();
  const { info } = useAppSelector((state) => state.user);
  const [launchGame] = useLaunchAggregatorGameMutation();

  const lockRef = useRef(false);
  const [isLaunching, setIsLaunching] = useState(false);
  const LOADING_TOAST_ID = 'aggregator-launch-loading';

  const launch = useCallback(
    async (gameCode: string) => {
      if (lockRef.current) return;

      if (info?.role?.name?.toLowerCase() === 'demo') {
        toast.error('Demo users are not allowed to play casino games.');
        return;
      }

      lockRef.current = true;
      setIsLaunching(true);
      toast.loading('Launching Game...', { id: LOADING_TOAST_ID });

      try {
        const res = await launchGame({ gameCode }).unwrap();
        if (res?.launchUrl) {
          navigate('/casino-playground', { state: { gameUrl: res.launchUrl } });
        } else {
          toast.error('Failed to get game URL');
        }
      } catch (error: any) {
        toast.error(error?.data?.message || 'Failed to launch game');
      } finally {
        toast.dismiss(LOADING_TOAST_ID);
        lockRef.current = false;
        setIsLaunching(false);
      }
    },
    [launchGame, navigate, info],
  );

  return { launch, isLaunching };
};
